import React, { useEffect, useState, useMemo } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import {
  assessmentStatusUrl,
  phlebotomyAssessmentStatusUrl,
} from "../../utils/constants";
import useApis from "../../services/useAPI";
import { Button } from "../../stories/Buttons/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { getTestNameFromType } from "../../utils";
import { toMatric } from "../../utils/utils";

function prettifyValue(rawValue) {
  let val = rawValue;
  // Join array items into a string
  if (Array.isArray(val)) {
    val = val.join(", ");
  }

  val = String(val || "").replace("_", " ");
  return val;
}

const lookupTable = {
  "Height and weight": 1,
  Smoking: 1,
  Alcohol: 1,
  "Physical activity": 2,
  "Medical conditions": 3,
  Medications: 3,
  Supplements: 3,
  "Menstrual cycle": 4,
  "Sexual health": 4,
};

// Helper function to create base assessment data
function createBaseAssessmentData(values) {
  return {
    "Height and weight": {
      "Height unit": values?.body?.heightUnits,
      height: values?.body?.height,
      "Weight unit": values?.body?.weightUnits,
      weight: values?.body?.weight,
      "Are you pregnant?": values?.body?.ispregnant,
      "Trimester": values?.body?.trimester,
    },
    Smoking: {
      "Do you smoke cigarettes?": values?.smoking?.smoking,
      "How many cigarettes (or equivalent) per day?":
        values?.smoking?.cigarettes_per_day,
      "How many years did you smoke?": values?.smoking?.how_many_year,
      "How many per day on average?": values?.smoking?.how_many_day_average,
    },
    Alcohol: {
      "Do you drink alcohol?": values?.alcohol?.alcohol,
      "How many units per week?": values?.alcohol?.alcohol_units_per_week,
    },
    "Physical activity": {
      "Do you do any physical activity?": values?.activity?.physical_activity,
      "Light activity, e.g. walking, stretching":
        values?.activity?.light_activity_hours_per_week,
      "Moderate activity, e.g. aerobics, golf":
        values?.activity?.moderate_activity_hours_per_week,
      "Vigorous activity, e.g. running, weight training":
        values?.activity?.vigorous_activity_hours_per_week,
    },
    "Medical conditions": {
      "Have you been diagnosed with any medical conditions?":
        values?.medical_conditions?.is_diagnosed,
      "Please tick all that apply": [
        ...(values?.medical_conditions?.condition || []),
      ],
      "Please specify cancer type and whether you are currently or recently (past 3 months) under treatment (chemo, radio, or immunotherapy).":
        values?.medical_conditions?.cancer_type || "",
      "Please specify which deficiency":
        values?.medical_conditions?.deficiency || "",
      "Please specify other conditions":
        values?.medical_conditions?.other_description || "",
    },
    Medications: {
      "Are you taking any regular medication?":
        values?.medications?.medication || "",
      "Please tick all that apply": [
        ...(values?.medications?.medications || []),
      ],
      "Please tell us the name, dosage, and frequency of your medication.": [
        values?.medications?.specify_medication || "",
      ],
      "Please specify other medications":
        values?.medications?.other_description || "",
    },
     Supplements: {
      "Are you taking any regular supplements?":
        values?.supplements?.taking_supplements || "",
      "Please tick all that apply": [
        ...(values?.supplements?.supplements || []),
      ],
       "Please tell us the name, dosage, and frequency of your medication.": [
        values?.supplements?.supplement_details || "",
      ],
      "Please specify other supplements":
        values?.supplements?.other_description || "",
    },
  };
}

/**
 * Displays a summary of health assessment data.
 * Allows users to view and edit specific sections.
 */

export function SimpleAssessmentSummary() {
  const { callApi, resultGet, isLoading, serverError } = useApis();
  const [assessmentData, setAssessmentData] = useState({}); // Initialize state for assessment data
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();
  const { test_type, bid, test_kit_id } = useParams();
  const [shData, setShData] = useState();
  const [error, setError] = useState("");
  const [hormoneData, setHormoneData] = useState();

  const isPhlebotomy = location?.pathname?.includes("boots");

  // Create base assessment data using useMemo
  const baseAssessmentData = useMemo(() => {
    if (!resultGet?.assessment_values) return {};

    const values = resultGet.assessment_values;
    return createBaseAssessmentData(values);
  }, [resultGet?.assessment_values]);

  useEffect(() => {
    const test_kit_id = location?.pathname?.split("/")[2];
    if (isPhlebotomy && test_type && bid) {
      callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
    } else if (test_kit_id && !test_type && !bid) {
      callApi(null, `${assessmentStatusUrl}${test_kit_id}`, "GET", true);
    }
  }, []);

  useEffect(() => {
    if (resultGet?.test_kit_details?.test_kit_type?.includes("sex")) {
      const values = resultGet?.assessment_values;
      setShData({
        "Sexual health": {
          "Are you sexually active?": values?.sexual_health?.sexually_active,
          "Your sexual partners are": values?.sexual_health?.partners,
          "How long ago did you last have sex?":
            values?.sexual_health?.recent_sex,
          "Have you had unprotected sex in the past 3 months?":
            values?.sexual_health?.had_unprotected_sex,
          "Which type of sex was unprotected?":
            values?.sexual_health?.unprotected_sex_type,
          "Have you ever been diagnosed with an STI?":
            values?.sexual_health?.sti_diagnosed,
          "Do you have any symptoms?": values?.sexual_health?.sti_symptoms,
          "Please specify symptoms":
            values?.sexual_health?.sti_symptoms_description,
          "Do you think you may have been exposed to an STI?":
            values?.sexual_health?.potential_sti_exposure,
          "Please specify exposure":
            values?.sexual_health?.potential_sti_exposure_type,
          "Please specify other STI exposure" : values?.sexual_health?.other_sti_exposure_type,
        },
      });
    } else if (
      resultGet?.test_kit_details?.test_kit_type?.includes("menopause") ||
      resultGet?.test_kit_details?.test_kit_type?.includes("polycystic")
    ) {
      const values = resultGet?.assessment_values;
      setHormoneData({
        "Menstrual cycle": {
          "What is your normal cycle length?": values?.menstrual?.cycle_length,
          "Which day of your cycle was this blood test taken on?":
            values?.menstrual?.day_of_cycle,
          "Are you taking contraception?": values?.menstrual?.contraception,
        },
      });
    }
  }, [resultGet]);

  useEffect(() => {
    if (
      baseAssessmentData?.["Height and weight"]?.[
        "Height unit"
      ]?.toLowerCase() === "imperial"
    ) {
      const convertedHeight = toMatric(
        baseAssessmentData["Height and weight"].height,
        baseAssessmentData["Height and weight"]["Height unit"],
      );

      baseAssessmentData["Height and weight"].height = convertedHeight.value;
      baseAssessmentData["Height and weight"]["Height unit"] =
        convertedHeight.unit;
    }
    if (resultGet && isPhlebotomy && test_type && bid) {
      const personal_details = resultGet?.phlebotomist_booking_info;

      // Add phlebotomy-specific data
      setAssessmentData({
        ...baseAssessmentData,
        "Personal details": {
          "First name": [personal_details?.first_name],
          "Last name": [personal_details?.last_name],
          "Date of birth": [personal_details?.dob],
          "Sex at birth": [personal_details?.sex],
          "Do you still identify with this gender?": [
            personal_details?.identify_with_gender ? "Yes" : "No",
          ],
          "Ethnicity": [personal_details?.ethnicity],
          "Primary phone number": [personal_details?.phone_number_primary],
          "Home postal code": [personal_details?.home_postal_code],
          "Home street address": [personal_details?.home_street_address],
          ...(personal_details?.home_street_address === "No results" && {
            "Address line 1": [personal_details?.address_line_1],
            "Address line 2": [personal_details?.address_line_2],
            "Home city": [personal_details?.home_city],
            "Home country": [personal_details?.home_country],
          }),
        },
        "Test type": {
          Test: [getTestNameFromType(personal_details?.test_type)],
        },
      }); // Set the state when data is available
    } else if (resultGet?.assessment_values && !test_type && !bid) {
      // Add additional data for non-phlebotomy case
      setAssessmentData({
        ...baseAssessmentData,
        ...shData,
        ...hormoneData,
      });
    }
  }, [
    resultGet,
    baseAssessmentData,
    shData,
    hormoneData,
    isPhlebotomy,
    test_type,
    bid,
  ]);

  useEffect(() => {
    setError(serverError);
  }, [serverError]);

  if (!resultGet && !error) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  // Render each section
  function renderSection(title, fields) {
    return (
      <Col xs={12} md={6} lg={4} className="mb-4 summary-card pe-4">
        <div className="assessment-details-wrapper card">
        <div className="assessment-details">
          <div className="summary-card-header d-flex justify-content-between">
            {" "}
            <h5 className="assessment-details-title">{title}</h5>
            {title === "Test type" ? "": resultGet?.can_be_edited && (
              <Button
                className={"asssessment-edit-btn"}
                edit={true}
                data-testid={title}
                label={"Edit"}
                onClick={() => {
                    if (!isPhlebotomy && !test_type && !bid) {
                    navigate(
                      `/assessment/${test_kit_id}/${lookupTable[title]}`,
                    );
                  }
                  auth?.setStep(lookupTable[title]);
                  auth?.setCompletedSteps(lookupTable[title]);
                  auth?.setEditReload(true);
                  if (isPhlebotomy && test_type && bid) {
                    if (title === "Test type") {
                      navigate(
                        `/bloods/boots/registration/${test_type}?edit=true?bid=${bid}`,
                      );
                    } else if (title === "Personal details") {
                      navigate(
                        `/bloods/boots/${test_type}/booking/confirm-details/${bid}?edit=true`,
                      );
                    } else if (resultGet?.assessment_values) {
                      navigate(
                        `/bloods/boots/assessment/${test_type}/${bid}/${lookupTable[title]}`,
                      );
                    }
                  } else {
                    navigate(
                      `/assessment/${test_kit_id}/${lookupTable[title]}`,
                    );
                  }
                }}
              />
            )}
          </div>
        </div>
        <div className="row">
          {Object.keys(fields).map((f) => {
            const prettyValue = prettifyValue(fields[f]);
            if (!prettyValue) return null;

            return (
              <p key={f} className={`mt-1 ${title === "Height and weight" ? "col-6" : "col-12"}`}>
                <small className="fs-18 ageo-medium">
                  {f
                    .replace("_", " ")
                    .replace(/^(.)/, (match) => match.toUpperCase())}
                </small>{" "}
                <br />
                <strong className="fs-22 ageo-bold">{prettyValue}</strong>
              </p>
            );
          })}
          </div>
        </div>
      </Col>
    );
  }

  return (
    <Row className="assessment-summary">
      <div className="d-flex gap-2 fs-1-5">
        <h1>Assessment details</h1>{" "}
      </div>
      <p className="mt-4 fs-22 mb-5 text-muted">
        Here are your health assessment details, related to{" "}
        <strong className="fw-600 text-teal">
          {resultGet?.test_kit_details?.test_kit_name ||
            getTestNameFromType(
              resultGet?.phlebotomist_booking_info?.test_type,
            )}
        </strong>
        . Our doctor will prepare a report based on your answers. You can edit the assessment until the sample arrives at our laboratory.
      </p>
     <div className="main-cards-wrapper d-flex flex-wrap"> {Object.keys(assessmentData).length > 0 ? (
        Object.keys(assessmentData).map((section) =>
          assessmentData[section]
            ? renderSection(section, assessmentData[section])
            : null,
        )
      ) : (
        <div className="text-center">
          {isLoading ? (
            <Spinner animation="border" className="my-custom-spinner" />
          ) : (
            <p>No assessment data available.</p>
          )}
        </div>
      )}</div>
    </Row>
  );
}
